import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import {
  PlayIcon,
  PauseIcon,
  ShareIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  MusicalNoteIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

interface Recording {
  id: string;
  title: string;
  description?: string;
  venueName?: string;
  startTime?: string;
  durationSeconds?: number;
  status: string;
  isPublic?: boolean;
  viewCount?: number;
  downloadCount?: number;
  tags?: string[];
  streamingUrl?: string;
  waveformData?: number[];
}

interface RecordingCardProps {
  recording: Recording;
  onPlay: (recording: Recording) => void;
  onNavigate: (recording: Recording) => void;
  onEdit: (recording: Recording) => void;
  onShare: (recording: Recording) => void;
  onDelete: (recording: Recording) => void;
  isPlaying?: boolean;
}

const RecordingCard: React.FC<RecordingCardProps> = ({
  recording,
  onPlay,
  onNavigate,
  onEdit,
  onShare,
  onDelete,
  isPlaying = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'text-green-600 dark:text-green-400';
      case 'recording':
        return 'text-red-600 dark:text-red-400';
      case 'uploading':
      case 'processing':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Use real waveform data if available, otherwise generate fallback
  const waveformBars = recording.waveformData && recording.waveformData.length > 0
    ? recording.waveformData.slice(0, 100) // Use up to 100 samples
    : Array.from({ length: 40 }, () => Math.random() * 100); // Fallback for recordings without waveform data

  return (
    <motion.div
      className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden group"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
      transition={{ duration: 0.2 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Waveform Background */}
      <div className="relative h-32 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 dark:from-blue-500/20 dark:via-purple-500/20 dark:to-pink-500/20 overflow-hidden">
        {/* Animated waveform bars */}
        <div className="absolute inset-0 flex items-center justify-around px-2">
          {waveformBars.map((height, index) => (
            <motion.div
              key={index}
              className="w-1 bg-gradient-to-t from-blue-500 to-purple-500 rounded-full opacity-40"
              style={{ height: `${height}%` }}
              animate={isPlaying ? {
                height: [`${height}%`, `${Math.random() * 100}%`, `${height}%`],
              } : {}}
              transition={{
                duration: 0.5 + Math.random(),
                repeat: isPlaying ? Infinity : 0,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-white/90 dark:from-gray-800/90 to-transparent" />

        {/* Status badge */}
        <div className="absolute top-3 right-3">
          <motion.div
            className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
              recording.status === 'ready'
                ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                : 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300'
            }`}
            animate={recording.status === 'recording' ? {
              scale: [1, 1.05, 1],
              opacity: [1, 0.8, 1],
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {recording.status === 'ready' && <CheckCircleIcon className="inline h-3 w-3 mr-1" />}
            {recording.status}
          </motion.div>
        </div>

        {/* Public indicator */}
        {recording.isPublic === true && (
          <div className="absolute top-3 left-3">
            <div className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-500/20 backdrop-blur-sm text-blue-700 dark:text-blue-300">
              Public
            </div>
          </div>
        )}

        {/* Play button overlay */}
        <AnimatePresence>
          {isHovered && recording.status === 'ready' && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.button
                onClick={() => onPlay(recording)}
                className="w-16 h-16 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? (
                  <PauseIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                ) : (
                  <PlayIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 ml-1" />
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h3
          onClick={() => onNavigate(recording)}
          className="font-semibold text-gray-900 dark:text-gray-100 text-lg mb-1 line-clamp-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          {recording.title}
        </h3>

        {/* Venue */}
        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
          <MusicalNoteIcon className="h-4 w-4" />
          <span className="line-clamp-1">{recording.venueName || 'Unknown Venue'}</span>
        </div>

        {/* Description */}
        {recording.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {recording.description}
          </p>
        )}

        {/* Tags */}
        {recording.tags && recording.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {recording.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {tag}
              </span>
            ))}
            {recording.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                +{recording.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <ClockIcon className="h-3.5 w-3.5" />
            <span>{formatDuration(recording.durationSeconds || 0)}</span>
          </div>
          <div className="flex items-center gap-1">
            <EyeIcon className="h-3.5 w-3.5" />
            <span>{recording.viewCount || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            <span>{recording.downloadCount || 0}</span>
          </div>
        </div>

        {/* Date */}
        <div className="text-xs text-gray-500 dark:text-gray-500 mb-3">
          {recording.startTime ? formatDistanceToNow(new Date(recording.startTime), { addSuffix: true }) : 'Unknown date'}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <motion.button
            onClick={() => onPlay(recording)}
            disabled={recording.status !== 'ready'}
            className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 ${
              recording.status === 'ready'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
            whileHover={recording.status === 'ready' ? { scale: 1.02 } : {}}
            whileTap={recording.status === 'ready' ? { scale: 0.98 } : {}}
          >
            {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
            <span>{isPlaying ? 'Pause' : 'Play'}</span>
          </motion.button>

          <motion.button
            onClick={() => onShare(recording)}
            className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ShareIcon className="h-4 w-4" />
          </motion.button>

          <motion.button
            onClick={() => onEdit(recording)}
            className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <PencilIcon className="h-4 w-4" />
          </motion.button>

          <motion.button
            onClick={() => onDelete(recording)}
            className="px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <TrashIcon className="h-4 w-4" />
          </motion.button>
        </div>
      </div>

      {/* Playing indicator */}
      {isPlaying && (
        <motion.div
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          style={{ transformOrigin: 'left' }}
        >
          <motion.div
            className="h-full bg-white/50"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default RecordingCard;
